import { FlatListNodeItem } from './FlatListNodeItem';
import { forwardRef, type SyntheticEvent, useEffect } from 'react';
import { useAuthContext } from '../../../../providers/AuthProvider';
import { PERMS } from '../../../../routes/permissions';
import { useFlatTasksListStore } from '../store/useFlatTasksListStore';
import type { DISPLAY_FIELD, ToDoListViewItemProps } from '../types';
import type { TaskToDoEntry } from '@nexelem/graphne-api';

type FlatListItemProps = ToDoListViewItemProps<TaskToDoEntry> & {
  displayField: DISPLAY_FIELD;
  isBatchChild?: boolean;
};

export const FlatListItem = forwardRef<HTMLDivElement, FlatListItemProps>(
  (
    { task, isCheckAllFilterChecked, onCheck, onMoreClick, displayField, isBatchChild = false }: FlatListItemProps,
    ref,
  ) => {
    const mapIdKey = task.id.toString();
    const flatTaskListCheckMap = useFlatTasksListStore((state) => state.flatTaskListCheckMap);
    const setNewItem = useFlatTasksListStore((state) => state.setNewFlatItem);
    const { hasPermission } = useAuthContext();
    const canEditPlan = hasPermission(PERMS.PERM_4);

    useEffect(() => {
      if (!flatTaskListCheckMap.has(mapIdKey)) {
        setNewItem({ taskId: mapIdKey, isCheckAllFilterChecked });
      }
    }, [isCheckAllFilterChecked, flatTaskListCheckMap, mapIdKey, setNewItem]);

    const handleTaskCheck = (taskId: string) => (evt: SyntheticEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onCheck(taskId);
    };

    return (
      <FlatListNodeItem
        displayField={displayField}
        isBatchChild={isBatchChild}
        isChecked={flatTaskListCheckMap.get(task.id.toString())?.checked || false}
        isDragDisabled={!canEditPlan}
        key={mapIdKey}
        ref={ref}
        setChecked={handleTaskCheck(mapIdKey)}
        task={task}
        onMoreClick={(event) => onMoreClick(event, task)}
      />
    );
  },
);

FlatListItem.displayName = 'FlatListItem';
